import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBook, faUser, faUsers } from "@fortawesome/free-solid-svg-icons";
import useFetch from "../../hooks/useFetch";
import MainLayout from "../../layouts/MainLayout";
import TalkingCharacter from "../../components/TalkingCharacter";

const formatDate = (date) => {
  if (!date) return ""; 
  const jsDate = new Date(date);
  if (isNaN(jsDate)) return "Invalid Date"; 

  const day = jsDate.getDate().toString().padStart(2, "0");
  const month = (jsDate.getMonth() + 1).toString().padStart(2, "0");
  const year = jsDate.getFullYear();
  return `${day}-${month}-${year}`;
};

const ReadfluencerDashboard = () => {
  const authState = useSelector((state) => state.authReducer);
  const [fetchData, { loading, error }] = useFetch();
  const [books, setBooks] = useState([]);
  const [view, setView] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const talkingText = `
    Dr. Vijay Vichare stepped out on a pleasant evening, looking forward to meeting his old friends. 
    It had been a while since they had all gathered, and tonight was about unwinding, reminiscing, and sharing stories. 
    As he engaged in laughter and conversation, he had no idea that something remarkable was happening in the background—something he had set in motion weeks ago.

    While he was enjoying his two-hour reunion, elsewhere, thousands of students were deeply engaged in reading, listening, and taking notes on the Think5Whys platform. 
    These were the same students Dr. Vichare had once inspired to explore structured thinking through the book. 
    Over time, his influence had grown, reaching 5000 students who had purchased Think5Whys based on his recommendation.

    That day alone, 1000 of these students actively participated—each reading, listening to, or making notes on five pages of the book. 
    Unbeknownst to Dr. Vichare, his efforts had just earned him Rs. 100 as passive income (Rs. 0.02 × 5 actions × 1000 students).

    But this was just the beginning.

    On an average month, these 5000 students were consistently engaging with Think5Whys, consuming around 500 pages each. The numbers spoke for themselves:

    0.02 Rs. × 500 actions × 5000 students = Rs. 50,000 per month

    While Dr. Vichare was simply enjoying an evening with friends, his impact was rippling across thousands of students. 
    The knowledge he had shared was not only transforming young minds but also generating a sustainable passive income—all because of the seeds he had planted earlier.

    This realization struck him later that night. It wasn’t just about the money; it was about the power of influence. 
    His words, his recommendations, and his belief in structured thinking had set in motion a movement.

    Dr. Vijay Vichare smiled.

    Tonight, while he laughed with his friends, elsewhere, a thousand minds were growing—thanks to him.
  `;

  const fetchBooks = useCallback(() => {
    const config = {
      url: "/mybooks/readfluencerdata",
      method: "POST",
      headers: { Authorization: authState.token },
      data: { readfluencerCode: authState.user.think5whyscode },
    };
    fetchData(config, { showSuccessToast: false })
      .then((data) => {
        if (data && Array.isArray(data.readfluencingbooks)) {
          setBooks(data.readfluencingbooks);
        } else {
          console.error("Invalid response format: Readfluencer data not found!");
        }
      })
      .catch((error) => console.error("Error fetching Readfluencer data:", error));
  }, [authState.token, fetchData]);

  useEffect(() => {
    if (authState.isLoggedIn) fetchBooks();
  }, [authState.isLoggedIn, fetchBooks]);

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error loading data!</p>;

  const totalReadfluencers = new Set(books.map((item) => item.riId)).size;
  const totalBooks = new Set(books.map((item) => item.bookId)).size;
  const totalLearners = new Set(books.map((item) => item.userId)).size;

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = books.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <MainLayout>
      <div className="p-6 bg-gray-600 min-h-screen">
        <h1 className="text-xl font-bold text-center mb-6">Readfluencer Dashboard</h1>
        <TalkingCharacter text={talkingText} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Card title="Total Readfluencers" count={totalReadfluencers} icon={faUsers} onClick={() => setView("Readfluencers")} />
          <Card title="Total Books" count={totalBooks} icon={faBook} onClick={() => setView("Books")} />
          <Card title="Total Learners" count={totalLearners} icon={faUser} onClick={() => setView("Users")} />
        </div>
        {view && <Table data={currentItems} view={view} totalItems={books.length} itemsPerPage={itemsPerPage} paginate={paginate} />}
      </div>
    </MainLayout>
  );
};

const Card = ({ title, count, icon, onClick }) => (
  <div className="p-6 bg-blue-700 shadow-lg rounded-lg flex items-center justify-between cursor-pointer hover:bg-blue-500 hover:text-white transition-all" onClick={onClick}>
    <div>
      <h2 className="text-lg font-semibold">{title}</h2>
      <p className="text-lg font-bold">{count}</p>
    </div>
    <FontAwesomeIcon icon={icon} className="text-lg" />
  </div>
);

const Table = ({ data, view, totalItems, itemsPerPage, paginate }) => (
  <div className="bg-blue-700 text-xs p-6 shadow-lg rounded-lg">
    <h2 className="text-lg font-semibold mb-4">{view} List</h2>
    <table className="w-full border-collapse border border-gray-300">
      <thead>
        <tr className="bg-gray-200">
          {view === "Readfluencers" && <><th className="border p-2">Readfluencer Name</th><th className="border p-2">Code</th><th className="border p-2">Date</th></>}
          {view === "Books" && <><th className="border p-2">Readfluencer Name</th><th className="border p-2">Code</th><th className="border p-2">Book Title</th><th className="border p-2">Date</th></>}
          {view === "Users" && <><th className="border p-2">Learner Name</th><th className="border p-2">Learner Code</th><th className="border p-2">Readfluencer Name</th><th className="border p-2">Readfluencer Code</th><th className="border p-2">Book Title</th><th className="border p-2">Date</th></>}
        </tr>
      </thead>
      <tbody>
        {data.map((item, index) => (
          <tr key={index} className="text-left border">
            {view === "Readfluencers" && <><td className="border p-2">{item.riName}</td><td className="border p-2">{item.riCode}</td><td className="border p-2">{formatDate(item.purchaseDate)}</td></>}
            {view === "Books" && <><td className="border p-2">{item.riName}</td><td className="border p-2">{item.riCode}</td><td className="border p-2">{item.bookTitle}</td><td className="border p-2">{formatDate(item.purchaseDate)}</td></>}
            {view === "Users" && <><td className="border p-2">{item.userName}</td><td className="border p-2">{item.userCode}</td><td className="border p-2">{item.riName}</td><td className="border p-2">{item.riCode}</td><td className="border p-2">{item.bookTitle}</td><td className="border p-2">{formatDate(item.purchaseDate)}</td></>}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default ReadfluencerDashboard;
